/** /notifications/rulesets — cross-tenant Notification_Ruleset catalog.
 *
 * Grid + create dialog; rows open the full-page editor (rules editing needs
 * the room). A null tenant is a system ruleset usable by every operator. No
 *  batch delete: `nested_ruleset` / notification-string FKs CASCADE, so
 * deletion is only offered on the editor page behind the 409 in-use guard
 * (tag-admin precedent). */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as rulesetsApi from '@/api/notificationRulesets';
import { MATCH_ANY, type RulesetListItem } from '@/api/notificationRulesets';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { pickedSentinel, TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { SYSTEM_TENANT } from './CreateNotificationDialog';
import { RulesetCreateDialog } from './RulesetCreateDialog';

const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

export function RulesetsListPage() {
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_notification_rulesets_manage');

  const [createOpen, setCreateOpen] = useState(false);
  // The operators (several, D1) or the System sentinel alone (D3); they follow
  // the console scope until picked here. Operators return their rulesets PLUS the
  // system ones (a system ruleset is usable by every operator); System alone
  // returns only the system ones.
  const [tenants, setTenants] = useScopedTenantsState();
  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const systemPicked = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) !== null;
  const tenantParam = systemPicked ? 'system' : tenantIds;

  useCreateIntent(() => setCreateOpen(true));

  const extra = useMemo(() => {
    const e: Record<string, string | number> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    return e;
  }, [tenantParam]);

  const columns = useMemo<GridColDef<RulesetListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
      {
        field: 'codename',
        headerName: 'Codename',
        width: 180,
        renderCell: (p) =>
          p.row.codename ? <span style={{ fontFamily: 'monospace' }}>{p.row.codename}</span> : null,
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: true,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        field: 'match_type',
        headerName: 'Match',
        width: 110,
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            label={p.row.match_type === MATCH_ANY ? 'ANY' : 'ALL'}
          />
        ),
      },
      {
        field: 'rules_count',
        headerName: 'Rules',
        width: 90,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <span title={p.row.updated_ts ?? ''}>{formatRelativeTime(p.row.updated_ts)}</span>
        ),
      },
    ],
    [],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      rulesetsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-rulesets"
        title="Rulesets"
        subtitle="Reusable groups of targeting rules — include them in notification rules, other rulesets, {% if-ruleset %} template tags and campaign audiences. A system ruleset has no owning operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New ruleset
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<RulesetListItem>
        queryKey={['notification_rulesets']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultSort={{ field: 'name', sort: 'asc' }}
        emptyMessage="No rulesets yet."
        onRowClick={(id) => navigate(`/notifications/rulesets/${id}`)}
        headerToolbar={{
          onResetAllFilters: () => setTenants([]),
          resetDisabled: tenants.length === 0,
          search: true,
          searchPlaceholder: 'Name, codename…',
          exportFileName: 'notification-rulesets',
        }}
      />

      {createOpen && (
        <RulesetCreateDialog
          open
          // The page's operators (D9); the System sentinel names none and opens on System.
          pageTenants={pageTenantsFrom(tenantIds, tenants)}
          defaultSystem={systemPicked}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </Box>
  );
}
